import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PATH_TO_WARNING_AUDIO, DATA_CHANNELS, getRobotIconPath } from "shared/config/setting";
import { message, Select, Input, Button, Tooltip } from "antd";
import { AudioOutlined, StopOutlined, SendOutlined, CloseOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { COMMAND_TOPICS, STATUS_TOPICS } from "shared/services/messaging/messageTypes";
import { useSpeechToText } from "features/robot-teleop/hooks/useSpeechToText";

import GamepadController from "features/robot-teleop/components/joystick/JoystickController";
import TwistKeyboardMoveCommander from "features/robot-teleop/components/keyboard/TwistKeyboardMoveCommander";
import "shared/assets/styles/components/demobot-control-page.css";
import LogViewer from "shared/components/ui/LogViewer";
import { useNotification } from "shared/contexts/UserNotificationProvider";
import { useServerConnection } from "shared/contexts/ServerConnectionContext";
import JoyKeyboardTopicCommander from "features/robot-teleop/components/keyboard/JoyKeyboardTopicCommander";
import { usePageTitle } from "shared/contexts/PageTitleContext";
import { useRobotStatus } from "features/robot-teleop/hooks/useRobotStatus";
import { useRobotCommands } from "features/robot-teleop/hooks/useRobotCommands";
import { useEventSubscription, getRobotEvent } from "shared/services/eventBus";
import { useRobotControlManager } from "features/robot-teleop/hooks/useRobotInfoService";
import {
  normalizeRobotStatus,
  ROBOT_STATUS_COMPARE_KEYS,
} from "features/robot-teleop/utils/robotStatus";
import RobotStatusPanel from "features/robot-teleop/components/robot-status/RobotStatusPanel";
import RobotConnectedUsers from "features/robot-teleop/components/robot-status/RobotConnectedUsers";
import { useRobotConnectedUsers } from "features/robot-teleop/hooks/useRobotConnectedUsers";
import { useAuth } from "shared/contexts/AuthProvider";
import { peerHasCamera, peerHasAudio } from "features/robot-teleop/utils/peerMedia";

// New WebRTC architecture
import {
  RobotConnectionManagerProvider,
  useConnectionManager,
} from "features/robot-teleop/contexts/RobotConnectionManagerContext";
import { usePeerConnection } from "features/robot-teleop/hooks/usePeerConnection";
import { usePeerMediaTracks } from "features/robot-teleop/hooks/usePeerMediaTracks";
import { usePeerDataChannel } from "features/robot-teleop/hooks/usePeerDataChannel";
import { PEER_STATES } from "features/robot-teleop/webrtc/peerStates";

// Per-peer link quality UI (badge, stalled-video overlay, chart, header indicator)
import "shared/assets/styles/components/peer-link.css";
import PeerTileBadge from "features/robot-teleop/components/peer-link/PeerTileBadge";
import PeerStallOverlay from "features/robot-teleop/components/peer-link/PeerStallOverlay";
import PeerLinkChart from "features/robot-teleop/components/peer-link/PeerLinkChart";
import ControlLinkIndicator from "features/robot-teleop/components/peer-link/ControlLinkIndicator";
import PeerTileControls from "features/robot-teleop/components/peer-link/PeerTileControls";


// ==================== PeerVideoPanel ====================
// Renders video/audio for a single peer, or a connect button if not connected.

/**
 * Attach a MediaStream to a <video>/<audio> element and (re)start playback.
 * Autoplay of unmuted audio can be blocked until the user interacts with the
 * page; in that case playback is retried on the next pointer/keyboard event.
 */
const attachStream = (el, stream, onPlaybackBlocked) => {
  if (!el) return;
  if (el.srcObject !== stream) {
    el.srcObject = stream || null;
  }
  if (!stream) return;
  const playPromise = el.play();
  if (playPromise && typeof playPromise.then === "function") {
    playPromise.then(() => onPlaybackBlocked?.(false)).catch(() => {
      // Autoplay blocked (unmuted audio, or a strict policy): a frame may be
      // decoded but nothing is shown/heard — surface it and retry on input.
      onPlaybackBlocked?.(true);
      const retry = () => {
        if (el.srcObject === stream) {
          el.play().then(() => onPlaybackBlocked?.(false)).catch(() => {});
        }
      };
      window.addEventListener("pointerdown", retry, { once: true });
      window.addEventListener("keydown", retry, { once: true });
    });
  }
};

const PeerVideoPanel = ({ peerId, className = "camera" }) => {
  const { t } = useTranslation();
  const { state, cameras, microphones, connect, remoteAudioEnabled } = usePeerConnection(peerId);
  const { videoStream, audioStream } = usePeerMediaTracks(peerId);
  const [videoBlocked, setVideoBlocked] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const videoElRef = useRef(null);
  const audioElRef = useRef(null);

  // Callback refs run when the element mounts AND whenever the stream changes.
  // The remote track (and thus the stream) arrives while the peer is still
  // CONNECTING, but the <video> element only mounts once it is CONNECTED — an
  // effect keyed on the stream alone would run before the element exists.
  const videoRef = useCallback((el) => {
    videoElRef.current = el;
    if (!videoStream) setVideoBlocked(false);
    attachStream(el, videoStream, setVideoBlocked);
  }, [videoStream]);
  const audioRef = useCallback((el) => {
    audioElRef.current = el;
    if (!audioStream) setAudioBlocked(false);
    attachStream(el, audioStream, setAudioBlocked);
  }, [audioStream]);

  const resumePlayback = useCallback(() => {
    const tryPlay = (el, setBlocked) => {
      if (!el || !el.srcObject) return;
      el.play().then(() => setBlocked(false)).catch(() => {});
    };
    tryPlay(videoElRef.current, setVideoBlocked);
    tryPlay(audioElRef.current, setAudioBlocked);
  }, []);

  const compact = className === "hiden-camera";
  // The badge (dot · id · grade · RTT · jitter · fps · sparkline) is rendered in
  // every state so each tile is always identified; it owns its own stats
  // subscription, so stats ticks never re-render this panel or the <video>.
  const badge = <PeerTileBadge peerId={peerId} compact={compact} />;
  // Connect / disconnect / reconnect and the per-peer audio toggles; an overlay
  // at the top-right of a video tile, inline on the audio-only chips (where it
  // also carries the Connect button).
  const tools = <PeerTileControls peerId={peerId} compact={compact} />;

  const centered = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 8,
  };

  if (state === PEER_STATES.IDLE || state === PEER_STATES.CLOSED) {
    return (
      <div className={`${className} connect-prompt`} style={compact ? undefined : centered}>
        {badge}
        {!compact && (
          <span style={{ color: "#aaa", fontSize: 12 }}>
            {t("teleop.tile.devices", { cameras: cameras.length, mics: microphones.length })}
          </span>
        )}
        {!compact && <Button size="small" onClick={connect}>{t("teleop.tile.connect")}</Button>}
        {tools}
      </div>
    );
  }

  if (state === PEER_STATES.FAILED) {
    return (
      <div className={`${className} connect-prompt`} style={compact ? undefined : centered}>
        {badge}
        {!compact && <span style={{ color: "#f66", fontSize: 12 }}>{t("teleop.tile.connectionFailed")}</span>}
        {!compact && <Button size="small" onClick={connect}>{t("teleop.tile.retry")}</Button>}
        {tools}
      </div>
    );
  }

  if (state === PEER_STATES.CONNECTING || state === PEER_STATES.RECONNECTING) {
    return (
      <div className={className} style={compact ? undefined : centered}>
        {badge}
        {!compact && (
          <span style={{ color: "#aaa" }}>
            {state === PEER_STATES.RECONNECTING ? t("teleop.tile.reconnecting") : t("teleop.tile.connecting")}
          </span>
        )}
        {tools}
      </div>
    );
  }

  // Render the elements when the robot declares the device OR a track actually arrived.
  const showVideo = !compact && (cameras.length > 0 || !!videoStream);
  const showAudio = microphones.length > 0 || !!audioStream;

  return (
    <div className={className}>
      {badge}
      {showVideo && (
        <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      )}
      {showAudio && <audio ref={audioRef} autoPlay muted={!remoteAudioEnabled} />}
      {showVideo && <PeerStallOverlay peerId={peerId} />}
      {(videoBlocked || audioBlocked) && (
        // Video blocked: centered over a scrim (nothing is shown anyway).
        // Audio only: a small corner button that leaves the video untouched —
        // browsers block unmuted autoplay on every fresh page load.
        <div className={`peer-playback-overlay ${videoBlocked ? "" : "audio-only"}`}>
          <Button type="primary" size="small" icon={<PlayCircleOutlined />} onClick={resumePlayback}>
            {videoBlocked ? t("teleop.tile.playVideo") : t("teleop.tile.enableAudio")}
          </Button>
        </div>
      )}
      {tools}
    </div>
  );
};

// ==================== RobotControlPageInner ====================
// The actual page content, inside the RobotConnectionManagerProvider.

const RobotControlPageInner = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const { robotId } = useParams();
  const { updatePageTitle } = usePageTitle();
  const { notifyError, notifyInfo } = useNotification();
  const { user: currentUser } = useAuth();
  const { t } = useTranslation();

  // Set page title
  useEffect(() => {
    updatePageTitle(t("teleop.pageTitle", { robotId }));
  }, [robotId, t]);

  // `robotClients` is conn-manager's name for the robot's peers; "peer" everywhere below
  const { loading, robotInfo: robot, robotClients: peerInfos, loadRobotInfo, loadRobotClientsInfo } = useRobotControlManager(robotId);
  const { initialize, connectAutoConnectPeers, controlPeerId } = useConnectionManager();
  const { connectionState: serverState, CONNECTION_STATES: SERVER_STATES } = useServerConnection();
  // Offers can only be delivered once the signaling socket is authenticated;
  // dialing earlier just burns a retry and races the reconnect-on-connect path.
  const signalingReady = serverState === SERVER_STATES.CONNECTED;

  const joyControlMapping = robot?.robot_type?.joy_control_mapping ?? null;

  // Initialize connection manager when the robot's peers are loaded (and dial the
  // auto-connect peers as soon as signaling is ready)
  useEffect(() => {
    if (peerInfos?.length) {
      initialize(peerInfos);
      if (signalingReady) connectAutoConnectPeers();
    }
  }, [peerInfos, initialize, connectAutoConnectPeers, signalingReady]);

  // Control peer data channel for sending commands
  const { send: controlChannelSend, isOpen: isControlChannelOpen } = usePeerDataChannel(controlPeerId, DATA_CHANNELS.CHAT);
  const { state: controlPeerState } = usePeerConnection(controlPeerId);

  const rtcConnectionState = controlPeerState === PEER_STATES.CONNECTED ? "connected" : "disconnected";

  // Readiness flags are read through refs inside the command handlers so the
  // handlers stay referentially stable and never act on a stale closure (the
  // keyboard/gamepad commanders keep calling the handler instance they captured).
  const isControlChannelOpenRef = useRef(isControlChannelOpen);
  useEffect(() => { isControlChannelOpenRef.current = isControlChannelOpen; }, [isControlChannelOpen]);
  const rtcConnectionStateRef = useRef(rtcConnectionState);
  useEffect(() => { rtcConnectionStateRef.current = rtcConnectionState; }, [rtcConnectionState]);

  const notifyErrorRef = useRef(notifyError);
  useEffect(() => { notifyErrorRef.current = notifyError; }, [notifyError]);

  // Keyboard repeat / gamepad polling would otherwise raise a toast every few
  // milliseconds while the robot is not ready: at most one per interval.
  const NOT_READY_NOTIFY_INTERVAL = 2000;
  const lastNotReadyNotifyRef = useRef(0);
  const notifyNotReady = useCallback((msg) => {
    const now = Date.now();
    if (now - lastNotReadyNotifyRef.current < NOT_READY_NOTIFY_INTERVAL) return;
    lastNotReadyNotifyRef.current = now;
    notifyErrorRef.current(msg);
  }, []);

  // Wrap channelSend to match the (channelLabel, data) signature expected by useRobotCommands.
  // A failed send throws so that CommandHandler reports a failure instead of a success.
  const channelSend = useCallback((_channelLabel, data) => {
    if (!controlChannelSend(data)) {
      throw new Error(`Data channel '${DATA_CHANNELS.CHAT}' is not open`);
    }
  }, [controlChannelSend]);

  const audioAlarm = useRef(null);

  const [connectionLogRecords, setConnectionLogRecords] = useState([]);
  const logRecordsRef = useRef([]);
  const logDisplaySize = 10;
  const maxLogRecords = logDisplaySize;

  // Throttle control for addLogRecord
  const lastLogTimeRef = useRef(0);
  const logUpdateTimerRef = useRef(null);
  const logThrottleDelay = 500;

  // ===== ROBOT STATUS MANAGEMENT =====
  // Standard `robot.status` topic ({ robot_info, robot_state, mission_info }); see
  // features/robot-teleop/utils/robotStatus.js. The flat `robot_status` message is deprecated.
  const [robotErrorInfo, setRobotErrorInfo] = useState(null);

  const robotStatusOptions = useMemo(
    () => ({
      throttle: 100,
      topic_name: STATUS_TOPICS.ROBOT_STATUS,
      transformFn: normalizeRobotStatus,
      compareKeys: ROBOT_STATUS_COMPARE_KEYS,
    }),
    []
  );

  const { status: robotStatus } = useRobotStatus(controlPeerId, robotStatusOptions);

  // Identity shown in the status panel, from the registered robot document; the
  // model the robot reports in robot_info takes precedence inside the panel.
  const robotIdentity = useMemo(
    () => ({
      robot_id: robot?.robot_id ?? robotId,
      icon_path: getRobotIconPath(robot),
      name: robot?.name ?? robotId,
      robot_type: robot?.robot_type?.name ?? t("teleop.unknownRobotType"),
    }),
    [robot, robotId, t]
  );

  // Who else is connected to the robot's peers (REST snapshot + live conn-manager events)
  const { users: connectedUsers } = useRobotConnectedUsers(peerInfos);

  // Use the new useRobotCommands hook
  const { sendCommand, sendTwist, sendJoy, sendKeyboard } = useRobotCommands(
    channelSend,
    {
      channelName: DATA_CHANNELS.CHAT,
      onError: (error) => {
        notifyError(t("teleop.notify.commandFailed", { message: error.message }));
      },
    }
  );

  // ===== LOG RECORD FUNCTION =====
  const addLogRecord = useCallback(
    (msg) => {
      const now = Date.now();
      if (now - lastLogTimeRef.current < 100) return;

      const trimmedMsg = msg.length > 100 ? msg.slice(0, 100) + "..." : msg;
      const logRecord = { Time: now, Message: trimmedMsg };

      logRecordsRef.current = [
        ...logRecordsRef.current.slice(-(maxLogRecords - 1)),
        logRecord,
      ];

      const waitingTime = logThrottleDelay - (now - lastLogTimeRef.current);

      if (waitingTime > 0) {
        if (logUpdateTimerRef.current) return;
        logUpdateTimerRef.current = setTimeout(() => {
          setConnectionLogRecords([...logRecordsRef.current]);
          lastLogTimeRef.current = Date.now();
          logUpdateTimerRef.current = null;
        }, waitingTime);
        return;
      }

      if (logUpdateTimerRef.current) {
        clearTimeout(logUpdateTimerRef.current);
        logUpdateTimerRef.current = null;
      }
      lastLogTimeRef.current = now;
      setConnectionLogRecords([...logRecordsRef.current]);
    },
    [maxLogRecords, logThrottleDelay]
  );

  const robotLogMessageEventPath = useMemo(
    () => getRobotEvent(controlPeerId, "log_message"),
    [controlPeerId]
  );

  const robotLogMessageHandler = useCallback((value, topicName) => {
    const displayValue = typeof value === "object" ? JSON.stringify(value) : String(value);
    const truncatedValue = displayValue.length > 200 ? displayValue.slice(0, 200) + "..." : displayValue;
    addLogRecord("[" + topicName + "] " + truncatedValue);
  }, [addLogRecord]);

  useEventSubscription(
    robotLogMessageEventPath,
    robotLogMessageHandler,
    {},
    [robotLogMessageEventPath, robotLogMessageHandler]
  );

  // ===== SETUP EFFECT =====
  useEffect(() => {
    loadRobotInfo(robotId);
    loadRobotClientsInfo(robotId);
  }, [robotId]);

  // ===== CLEANUP EFFECT =====
  useEffect(() => {
    return () => {
      if (logUpdateTimerRef.current) {
        clearTimeout(logUpdateTimerRef.current);
        logUpdateTimerRef.current = null;
      }
    };
  }, []);

  const playAlarm = (play) => {
    const audio = audioAlarm.current;
    if (play) audio.play();
    else audio.pause();
  };

  useEffect(() => {
    if (!robotErrorInfo) playAlarm(false);
    else playAlarm(true);
  }, [robotErrorInfo]);

  // Control mode: 'twist', 'joy', 'gamepad'
  const [controlMode, setControlMode] = useState("joy");
  const [lastSentText, setLastSentText] = useState("");

  // ===== SPEECH TO TEXT ======
  const {
    textInput: sttText,
    setTextInput: setSttText,
    interimText: sttInterimText,
    isRecording: sttIsRecording,
    isSpeechSupported,
    toggleRecording: sttToggleRecording,
    startRecording: sttStartRecording,
    stopRecording: sttStopRecording,
    lang: sttLang,
    setLang: setSttLang,
  } = useSpeechToText({
    initialLang: "ko-KR",
    onError: (msg) => notifyError(msg),
  });

  const sttIsRecordingRef = useRef(sttIsRecording);
  useEffect(() => { sttIsRecordingRef.current = sttIsRecording; }, [sttIsRecording]);

  const sttTextRef = useRef(sttText);
  useEffect(() => { sttTextRef.current = sttText; }, [sttText]);

  const sttPTTActiveRef = useRef(false);

  const PTT_KEY_CODE = "Backquote";
  const TOGGLE_STT_KEY_CODE = "KeyP";
  const RESET_INSTRUCTION_KEY_CODE = "BracketRight";
  const SEND_INSTRUCTION_KEY_CODE = "Backslash";

  useEffect(() => {
    if (!isSpeechSupported) return;
    const handleKeyDown = (e) => {
      if (e.code !== PTT_KEY_CODE || e.repeat) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (sttIsRecordingRef.current) return;
      sttPTTActiveRef.current = true;
      sttStartRecording();
    };
    const handleKeyUp = (e) => {
      if (e.code !== PTT_KEY_CODE || !sttPTTActiveRef.current) return;
      sttPTTActiveRef.current = false;
      sttStopRecording();
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [isSpeechSupported, sttStartRecording, sttStopRecording]);

  const handleSendInstruction = useCallback((text) => {
    if (!text?.trim()) return;
    if (rtcConnectionStateRef.current !== "connected" || !isControlChannelOpenRef.current) {
      notifyNotReady(t("teleop.notify.connectionNotReady"));
      return;
    }
    const sent = sendCommand(COMMAND_TOPICS.NLP_INSTRUCTION, { text: text.trim(), lang: sttLang });
    if (!sent) return;
    setLastSentText(text.trim());
    setSttText("");
  }, [sendCommand, sttLang, setSttText, notifyNotReady, t]);

  const handleSendInstructionRef = useRef(handleSendInstruction);
  useEffect(() => { handleSendInstructionRef.current = handleSendInstruction; }, [handleSendInstruction]);

  const handleResetInstruction = useCallback(() => {
    if (sttIsRecordingRef.current) sttStopRecording();
    setSttText("");
  }, [sttStopRecording, setSttText]);

  const handleResetInstructionRef = useRef(handleResetInstruction);
  useEffect(() => { handleResetInstructionRef.current = handleResetInstruction; }, [handleResetInstruction]);

  useEffect(() => {
    if (!isSpeechSupported) return;
    const handleKeyDown = (e) => {
      if (e.code !== TOGGLE_STT_KEY_CODE || e.repeat) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      sttToggleRecording();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSpeechSupported, sttToggleRecording]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code !== SEND_INSTRUCTION_KEY_CODE) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      handleSendInstructionRef.current(sttTextRef.current);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code !== RESET_INSTRUCTION_KEY_CODE) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      handleResetInstructionRef.current();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const notifySent = useCallback((logLine, quiet) => {
    if (!quiet) {
      messageApi.open({ type: "info", content: t("teleop.notify.commandSent"), style: { marginTop: "10vh" } });
    }
    addLogRecord(logLine);
  }, [messageApi, addLogRecord, t]);

  const handleOnCommandCreated = useCallback(
    (command, quite = false) => {
      if (!isControlChannelOpenRef.current) {
        notifyNotReady(t("teleop.notify.dataChannelNotReady"));
        return;
      }
      if (sendTwist(command)) {
        notifySent(`cmd_vel sent: ${JSON.stringify(command)}`, quite);
      }
    },
    [sendTwist, notifySent, notifyNotReady, t]
  );

  const handleKeyboardTopicCreated = useCallback(
    (topicValue, quite = false) => {
      if (!isControlChannelOpenRef.current) {
        notifyNotReady(t("teleop.notify.dataChannelNotReady"));
        return;
      }
      if (sendKeyboard(topicValue)) {
        notifySent(`keyboard sent: ${JSON.stringify(topicValue)}`, quite);
      }
    },
    [sendKeyboard, notifySent, notifyNotReady, t]
  );

  const handleOnJoystickCommandCreated = useCallback(
    (joyMsg, quite = false) => {
      if (!isControlChannelOpenRef.current || rtcConnectionStateRef.current !== "connected") {
        notifyNotReady(t("teleop.notify.connectionNotReady"));
        return;
      }
      if (sendJoy(joyMsg)) {
        notifySent(`joy sent: ${JSON.stringify(joyMsg)}`, quite);
      }
    },
    [sendJoy, notifySent, notifyNotReady, t]
  );

  const handleJoyKeyboardTopicCreated = useCallback(
    (joyValue, quite = false) => {
      if (!isControlChannelOpenRef.current) {
        notifyNotReady(t("teleop.notify.dataChannelNotReady"));
        return;
      }
      if (sendJoy(joyValue)) {
        notifySent(`joy sent: ${JSON.stringify(joyValue)}`, quite);
      }
    },
    [sendJoy, notifySent, notifyNotReady, t]
  );

  const cameraPeers = (peerInfos || []).filter((peer) => peer?.id && peerHasCamera(peer));
  const audioPeers = (peerInfos || []).filter((peer) => peer?.id && !peerHasCamera(peer) && peerHasAudio(peer));

  return (
    <div className="robot-control-body robot-control-v2">
      <div className="status-panel">
        {contextHolder}
        <RobotStatusPanel RobotStatus={robotStatus} RobotInfo={robotIdentity}>
          <RobotConnectedUsers Users={connectedUsers} CurrentUsername={currentUser?.username ?? null} />
        </RobotStatusPanel>
        <audio ref={audioAlarm} id="alarm-sound" src={PATH_TO_WARNING_AUDIO} loop />
      </div>

      <PeerLinkChart />

      <div className="cameras-panel">
        <div className={`camera-grid ${cameraPeers.length === 1 ? "single-tile" : ""}`}>
          {cameraPeers.map((peer) => (
            <PeerVideoPanel key={peer.id} peerId={peer.id} className="camera" />
          ))}
        </div>
        {audioPeers.length > 0 && (
          <div className="audio-peers">
            {audioPeers.map((peer) => (
              <PeerVideoPanel key={peer.id} peerId={peer.id} className="hiden-camera" />
            ))}
          </div>
        )}
      </div>

      <div className="control-panel">
        <div className="control-header">
          <div className="control-mode-section">
            <label className="control-label">{t("teleop.controlMode.label")}</label>
            <Select
              value={controlMode}
              onChange={(value) => setControlMode(value)}
              style={{ width: 200 }}
              options={[
                { value: "twist", label: <span>{t("teleop.controlMode.twist")}</span> },
                { value: "joy", label: <span>{t("teleop.controlMode.joy")}</span> },
                { value: "gamepad", label: <span>{t("teleop.controlMode.gamepad")}</span> },
              ]}
            />
          </div>
          <ControlLinkIndicator />
        </div>

        <div className="control-interface-section">
          {controlMode === "gamepad" && (
            <div className="gamepad-container">
              <GamepadController
                onGamepadStateUpdated={(joyMsg) => handleOnJoystickCommandCreated(joyMsg, true)}
                showVirtualJoystick={true}
                joyControlMapping={joyControlMapping}
              />
            </div>
          )}
          {controlMode === "twist" && (
            <div className="keyboard-container">
              <TwistKeyboardMoveCommander EnableKeyboard={true} OnCommandCreated={handleOnCommandCreated} />
            </div>
          )}
          {controlMode === "joy" && (
            <div className="keyboard-container">
              <JoyKeyboardTopicCommander
                OnCommandCreated={handleJoyKeyboardTopicCreated}
                EnableKeyboard={true}
                ThrottleInterval={80}
                JoyControlMapping={joyControlMapping}
              />
            </div>
          )}
        </div>
      </div>

      <div className="log-panel" style={{ overflow: "hidden" }}>
        {t("teleop.logTitle")}
        <LogViewer LogRecords={connectionLogRecords} displaySize={logDisplaySize} />
      </div>

      <div className="stt-panel">
        <div className="stt-input-row">
          <label style={{ color: "#fff", whiteSpace: "nowrap", margin: 0 }}>{t("teleop.instruction.label")}</label>
          <Select
            value={sttLang}
            onChange={(val) => setSttLang(val)}
            disabled={sttIsRecording}
            size="small"
            className="stt-lang-select"
            options={[
              { value: "ko-KR", label: "KR" },
              { value: "en-US", label: "EN" },
              { value: "vi-VN", label: "VN" },
            ]}
          />
          <Input
            placeholder={sttIsRecording ? (sttInterimText || t("teleop.instruction.listening")) : t("teleop.instruction.placeholder")}
            value={sttText + (sttInterimText ? ` [${sttInterimText}]` : "")}
            onChange={(e) => { if (!sttIsRecording) setSttText(e.target.value); }}
            onPressEnter={() => handleSendInstruction(sttText)}
            style={{ flex: 1 }}
            suffix={
              isSpeechSupported ? (
                <Tooltip title={sttIsRecording ? t("teleop.instruction.stopRecording") : t("teleop.instruction.startRecording")}>
                  <Button
                    type={sttIsRecording ? "primary" : "text"}
                    danger={sttIsRecording}
                    size="small"
                    icon={sttIsRecording ? <StopOutlined /> : <AudioOutlined />}
                    onClick={sttToggleRecording}
                    style={{ border: "none", boxShadow: "none" }}
                  />
                </Tooltip>
              ) : null
            }
          />
          <Tooltip title={rtcConnectionState !== "connected" ? t("teleop.instruction.robotNotConnected") : t("teleop.instruction.send")}>
            <Button
              type="primary"
              shape="circle"
              icon={<SendOutlined />}
              className="stt-send-btn"
              disabled={!sttText.trim() || rtcConnectionState !== "connected"}
              onClick={() => handleSendInstruction(sttText)}
            />
          </Tooltip>
          <Tooltip title={t("teleop.instruction.clear")}>
            <Button
              shape="circle"
              icon={<CloseOutlined />}
              className="stt-reset-btn"
              disabled={!sttText.trim() && !sttIsRecording}
              onClick={handleResetInstruction}
            />
          </Tooltip>
        </div>
        {lastSentText && (
          <div className="stt-last-sent">
            <span className="stt-last-sent-label">{t("teleop.instruction.lastSent")}</span>
            <span className="stt-last-sent-text">{lastSentText}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== Wrapper ====================
// Wraps the page with RobotConnectionManagerProvider.

const RobotControlPage = () => {
  const { robotId } = useParams();
  return (
    <RobotConnectionManagerProvider robotId={robotId} trickleICE={true}>
      <RobotControlPageInner />
    </RobotConnectionManagerProvider>
  );
};

export default RobotControlPage;
